import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Outlet, Link } from 'react-router-dom'
import { useMainScrollRestoration } from '../hooks/useScrollRestoration'
import { isCrmCoreRole } from '@crm/shared'
import { roleCaption, roleFullLabel } from '../utils/roleCaption'
import { useAuthStore } from '../../modules/auth/authStore'
import { useRealtimeStore } from '../realtime/realtimeStore'
import { getExchangeRates } from '../api/exchangeRates.api'
import { BrandMark } from '../../components/BrandMark'
import { NotificationBell } from '../../modules/notifications/NotificationBell'
import { useTabBadge } from '../../modules/notifications/useTabBadge'
import { RatesReminderModal } from '../../modules/exchange-rates/RatesReminderModal'
import { AnnouncementBanner } from '../../modules/announcements/AnnouncementBanner'
import { AnnouncementModal } from '../../modules/announcements/AnnouncementModal'
import { IconSettings, IconLogout, IconHamburger, IconSidebarToggle } from './sidebarIcons'
import { SidebarNav } from './SidebarNav'
import { useSidebarCollapsed } from './useSidebarCollapsed'

// ─── Внутреннее содержимое сайдбара ───────────────────────────────────────────

function SidebarContent({
  open,
  role,
  user,
  wsStatus,
  onNavClick,
  onToggle,
  logout,
}: {
  open: boolean
  role: string
  user: { name?: string | null; email?: string } | null
  wsStatus: string
  onNavClick?: () => void
  /** Свернуть/развернуть меню (только десктоп; в мобильном дравере кнопки нет). */
  onToggle?: () => void
  logout: () => void
}) {
  return (
    <div className="flex flex-col h-full">
      {/* Шапка */}
      <div className="h-[49px] flex items-center gap-2 px-3 border-b border-gray-100 shrink-0">
        {onToggle && !open ? (
          // Свёрнутое меню: логотип при наведении превращается в квадратик
          // «развернуть», как в Claude.
          <button
            type="button"
            onClick={onToggle}
            data-testid="sidebar-toggle"
            aria-label="Развернуть меню"
            title="Развернуть меню"
            className="group relative flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
          >
            <BrandMark size={32} className="group-hover:opacity-0 transition-opacity" />
            <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <IconSidebarToggle />
            </span>
          </button>
        ) : (
          <BrandMark size={32} className="flex-shrink-0" />
        )}
        {open && (
          <div className="flex-1 leading-tight overflow-hidden min-w-0">
            <div className="font-semibold text-gray-900 text-sm whitespace-nowrap">micro·hub</div>
            {role && (
              <div className="text-xs text-gray-400 truncate" title={roleFullLabel(role)}>
                {roleCaption(role)}
              </div>
            )}
          </div>
        )}
        {onToggle && open && (
          <button
            type="button"
            onClick={onToggle}
            data-testid="sidebar-toggle"
            aria-label="Свернуть меню"
            title="Свернуть меню"
            className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-800 transition-colors"
          >
            <IconSidebarToggle />
          </button>
        )}
      </div>

      {/* Навигация (двухуровневая) */}
      <SidebarNav open={open} role={role} onNavClick={onNavClick} onExpand={onToggle} />

      {/* Подвал */}
      <div className="p-2 border-t border-gray-100 space-y-0.5 shrink-0">
        {/* Настройки аккаунта */}
        <Link
          to="/settings"
          onClick={onNavClick}
          title={!open ? 'Настройки аккаунта' : undefined}
          className={`flex items-center py-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-brand-600 transition-colors ${open ? 'gap-3 px-2.5' : 'justify-center'}`}
        >
          <IconSettings />
          {open && <span className="text-xs whitespace-nowrap">Настройки аккаунта</span>}
        </Link>

        {/* Имя пользователя */}
        <div
          className={`flex items-center py-1 overflow-hidden ${open ? 'gap-3 px-2.5' : 'justify-center'}`}
        >
          <div className="flex-shrink-0 w-[18px] h-[18px] rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-[10px] font-medium">
            {(user?.name || user?.email || '?')[0].toUpperCase()}
          </div>
          {open && (
            <span className="text-xs text-gray-400 truncate">{user?.name || user?.email}</span>
          )}
        </div>

        {/* Выход + индикатор WS */}
        <div className={`flex items-center py-2 ${open ? 'gap-3 px-2.5' : 'justify-center'}`}>
          <button
            onClick={() => {
              onNavClick?.()
              logout()
            }}
            title={!open ? 'Выйти' : undefined}
            className="flex items-center gap-3 text-gray-500 hover:text-gray-900 transition-colors"
          >
            <IconLogout />
            {open && <span className="text-xs whitespace-nowrap">Выйти</span>}
          </button>
          {open && (
            <span
              title={
                wsStatus === 'connected'
                  ? 'Реалтайм: подключён'
                  : wsStatus === 'connecting'
                    ? 'Реалтайм: подключение…'
                    : 'Реалтайм: отключён'
              }
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                flexShrink: 0,
                background:
                  wsStatus === 'connected'
                    ? '#16a34a'
                    : wsStatus === 'connecting'
                      ? '#ca8a04'
                      : '#9ca3af',
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Строка курсов валют ──────────────────────────────────────────────────────

function ExchangeRateBar() {
  // Курсы — ролям CRM до этапа 3 и бухгалтеру (на API тот же белый список);
  // прочим ролям этапа 3 строка не запрашивается и не показывается (23.09.2026).
  const role = useAuthStore((s) => s.user?.role)
  const { data: rates } = useQuery({
    queryKey: ['exchange-rates'],
    queryFn: getExchangeRates,
    staleTime: 60 * 1000,
    enabled: isCrmCoreRole(role ?? '') || role === 'ACCOUNTANT',
  })

  if (!rates) return null

  return (
    <div className="shrink-0 relative z-20 h-7 bg-gray-50 border-b border-gray-100 flex items-center justify-center gap-4 sm:gap-6 text-xs text-gray-500 px-2">
      <span className="flex items-center gap-1">
        <span className="text-green-600 font-medium">$</span>
        <span className="font-mono">{rates.USD.toFixed(2)}</span>
        <span className="text-gray-300">₽</span>
      </span>
      <span className="flex items-center gap-1">
        <span className="text-blue-600 font-medium">€</span>
        <span className="font-mono">{rates.EUR.toFixed(2)}</span>
        <span className="text-gray-300">₽</span>
      </span>
      <span className="flex items-center gap-1">
        <span className="text-red-600 font-medium">¥</span>
        <span className="font-mono">{rates.CNY.toFixed(2)}</span>
        <span className="text-gray-300">₽</span>
      </span>
    </div>
  )
}

// ─── Layout ───────────────────────────────────────────────────────────────────

export function Layout() {
  const { user, logout } = useAuthStore()
  const role = user?.role ?? ''
  const wsStatus = useRealtimeStore((s) => s.status)
  // Бейдж непрочитанных уведомлений в title и favicon — пока пользователь
  // в другой вкладке.
  useTabBadge()
  // Десктоп: меню сворачивается и разворачивается кнопкой-квадратиком в шапке,
  // состояние запоминается в браузере (раньше — раскрытие по наведению мыши).
  const [collapsed, toggleCollapsed] = useSidebarCollapsed()
  const open = !collapsed
  // Мобильный: дравер по бургер-кнопке
  const [mobileOpen, setMobileOpen] = useState(false)

  const mainRef = useMainScrollRestoration() // восстановление скролла main при возврате

  const sidebarProps = {
    role,
    user,
    wsStatus,
    logout: () => void logout(),
  }

  return (
    <div className="h-screen flex flex-col lg:flex-row overflow-hidden">
      {/* ── Мобильная шапка (< lg) ── */}
      <header className="lg:hidden shrink-0 h-12 bg-white border-b border-gray-200 flex items-center px-3 gap-3 z-30">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-1 text-gray-500 hover:text-gray-800 transition-colors"
          aria-label="Открыть меню"
        >
          <IconHamburger />
        </button>
        <div className="flex flex-1 items-center gap-2 min-w-0">
          <BrandMark size={28} className="flex-shrink-0" />
          <span className="font-semibold text-gray-900 text-sm whitespace-nowrap">micro·hub</span>
          {role && (
            <span className="text-xs text-gray-400 truncate min-w-0" title={roleFullLabel(role)}>
              {roleCaption(role)}
            </span>
          )}
        </div>
        <NotificationBell />
      </header>

      {/* ── Затемнение при открытом мобильном дравере ── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Мобильный дравер (< lg) — фиксированный оверлей ── */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col transition-transform duration-200 lg:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent {...sidebarProps} open={true} onNavClick={() => setMobileOpen(false)} />
      </div>

      {/* ── Десктопный сайдбар (≥ lg) — свёрнут/развёрнут кнопкой в шапке ── */}
      <aside
        data-collapsed={collapsed ? 'true' : 'false'}
        className={`hidden lg:flex flex-col ${open ? 'w-56' : 'w-14'} shrink-0 bg-white border-r border-gray-200 transition-[width] duration-200 overflow-hidden`}
      >
        <SidebarContent {...sidebarProps} open={open} onToggle={toggleCollapsed} />
      </aside>

      {/* ── Основной контент ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="hidden lg:flex shrink-0 items-center border-b border-gray-100 bg-white pr-3">
          <div className="flex-1">
            <ExchangeRateBar />
          </div>
          <NotificationBell />
        </div>
        <div className="lg:hidden">
          <ExchangeRateBar />
        </div>
        {/* Объявления из «Пульта проектов» — плашкой под шапкой */}
        <AnnouncementBanner />
        <main ref={mainRef} className="flex-1 overflow-auto p-3 lg:p-6 min-w-0">
          <Outlet />
        </main>
      </div>

      <RatesReminderModal />
      {/* После напоминания о курсах: окно объявления оказывается поверх него */}
      <AnnouncementModal />
    </div>
  )
}
